import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Mailbox, Mail, Plug, RefreshCw, AlertCircle, ChevronDown, ChevronRight,
  Hotel, MapPin, Car, Train, Plane, Ticket, Mails, MessageSquare, Send, Calendar,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiErrorMessage } from "@/lib/api";
import { RESERVA_TYPE_LABELS, RESERVA_STATUS_LABELS, RESERVA_STATUS_CLASS } from "@/pages/trips/shared";
import TripEmailComposer from "@/pages/trips/TripEmailComposer";

const GMAIL_PRESET = { provider: "gmail", imap_host: "imap.gmail.com", imap_port: 993, smtp_host: "smtp.gmail.com", smtp_port: 465, smtp_ssl: true, smtp_starttls: false };
const OUTLOOK_PRESET = { provider: "outlook", imap_host: "outlook.office365.com", imap_port: 993, smtp_host: "smtp.office365.com", smtp_port: 587, smtp_ssl: false, smtp_starttls: true };
const IONOS_PRESET = { provider: "ionos", imap_host: "imap.ionos.es", imap_port: 993, smtp_host: "smtp.ionos.es", smtp_port: 465, smtp_ssl: true, smtp_starttls: false };
const ZOHO_PRESET = { provider: "zoho", imap_host: "imap.zoho.eu", imap_port: 993, smtp_host: "smtp.zoho.eu", smtp_port: 465, smtp_ssl: true, smtp_starttls: false };
const PRESETS = { gmail: GMAIL_PRESET, outlook: OUTLOOK_PRESET, ionos: IONOS_PRESET, zoho: ZOHO_PRESET };

const TYPE_ICON = {
  alojamiento: Hotel, actividad: MapPin, entradas: Ticket,
  transfer: Car, tren: Train, vuelo: Plane,
};

function statusDot(status) {
  if (status === "confirmacion") return { cls: "bg-emerald-500 ring-emerald-200", label: "Re-confirmado por el proveedor" };
  if (status === "reserva") return { cls: "bg-amber-400 ring-amber-200", label: "Confirmado — esperando re-confirmación" };
  if (status === "contacto") return { cls: "bg-sky-400 ring-sky-200", label: "Contactado — esperando respuesta" };
  if (status === "cancelada") return { cls: "bg-gray-400 ring-gray-200", label: "Cancelada" };
  return { cls: "bg-gray-300 ring-gray-100", label: "Sin contactar" };
}

function fmtDayHeader(iso) {
  if (!iso) return "Sin fecha";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  const s = d.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const PROVIDER_GUIDE = {
  gmail: {
    what: "Google no permite que otras aplicaciones usen tu contraseña normal. Por eso genera una clave de 16 letras, solo para SOFI, que se llama «contraseña de aplicación». La creas tú en tu cuenta de Google en 1 minuto y SOFI la guarda cifrada: solo te la pediremos esta vez.",
    link: "https://myaccount.google.com/apppasswords",
    linkLabel: "Abrir «Contraseñas de aplicación» de Google",
    steps: [
      "Pulsa el botón de arriba (te pedirá iniciar sesión con tu correo de trabajo).",
      "Si Google dice que no está disponible, activa antes la «Verificación en 2 pasos» en myaccount.google.com/security y vuelve.",
      "En «Nombre de la aplicación» escribe SOFI y pulsa Crear.",
      "Google te muestra 16 letras en 4 grupos (ej. abcd efgh ijkl mnop). Cópialas y pégalas aquí abajo; los espacios no importan.",
    ],
    note: "No es la contraseña con la que entras en Gmail. Si la cambias algún día, SOFI seguirá funcionando: la clave de aplicación es independiente.",
  },
  outlook: {
    what: "Microsoft bloquea el uso de tu contraseña normal desde otras aplicaciones cuando tienes verificación en 2 pasos (MFA). Necesitas una «contraseña de aplicación» generada en tu cuenta Microsoft; SOFI la guarda cifrada y solo te la pide esta vez.",
    link: "https://mysignins.microsoft.com/security-info",
    linkLabel: "Abrir «Información de seguridad» de Microsoft",
    steps: [
      "Pulsa el botón de arriba e inicia sesión con tu correo de trabajo.",
      "Pulsa «Agregar método de inicio de sesión» → «Contraseña de aplicación» (si no aparece, pide al administrador de Microsoft 365 que habilite las contraseñas de aplicación).",
      "Ponle de nombre SOFI y pulsa Siguiente.",
      "Copia la contraseña que te muestra (solo se ve una vez) y pégala aquí abajo.",
    ],
    note: "Si tu empresa tiene desactivado SMTP autenticado, el administrador debe activarlo en el centro de administración de Microsoft 365 → Usuarios → Correo → «SMTP autenticado».",
  },
  ionos: {
    what: "IONOS acepta la contraseña normal de tu buzón de correo (la misma del webmail). SOFI la guarda cifrada y solo te la pide esta vez.",
    link: "https://mail.ionos.es",
    linkLabel: "Abrir el webmail de IONOS para comprobar tu contraseña",
    steps: ["Usa el correo completo (nombre@tudominio.com) y la contraseña del buzón.", "Si falla, cámbiala desde el panel de IONOS → Correo → Buzón → Contraseña."],
    note: "",
  },
  zoho: {
    what: "Zoho exige una «contraseña específica de aplicación» cuando tienes verificación en 2 pasos. SOFI la guarda cifrada y solo te la pide esta vez.",
    link: "https://accounts.zoho.eu/home#security/security_pwd",
    linkLabel: "Abrir «Contraseñas específicas de aplicación» de Zoho",
    steps: ["Pulsa el botón de arriba e inicia sesión.", "En «Contraseñas específicas de aplicación» pulsa Generar, ponle de nombre SOFI.", "Copia la contraseña y pégala aquí abajo."],
    note: "",
  },
  custom: {
    what: "Introduce los datos SMTP/IMAP que te facilite tu proveedor de correo. Si tu cuenta tiene verificación en 2 pasos, normalmente necesitarás una contraseña de aplicación generada en el panel del proveedor.",
    link: "",
    linkLabel: "",
    steps: [],
    note: "",
  },
};

function friendlyConnectError(e, provider) {
  const raw = apiErrorMessage(e, "No se pudo conectar");
  const low = String(raw).toLowerCase();
  if (low.includes("autenticaci") || low.includes("username and password not accepted") || low.includes("invalid credentials") || low.includes("535")) {
    if (provider === "gmail") return "Google ha rechazado la clave. Casi siempre es porque se ha escrito la contraseña normal de Gmail en vez de la «contraseña de aplicación» de 16 letras, o porque la Verificación en 2 pasos no está activada. Sigue la guía de arriba y vuelve a probar.";
    if (provider === "outlook") return "Microsoft ha rechazado la clave. Comprueba que has pegado una «contraseña de aplicación» (no la normal) y que SMTP autenticado está habilitado para tu buzón.";
    return "El servidor ha rechazado el usuario o la contraseña. Revisa que el correo esté completo y la contraseña sea la correcta (o una contraseña de aplicación si tienes verificación en 2 pasos).";
  }
  if (low.includes("unexpectedly closed") || low.includes("timed out") || low.includes("timeout") || low.includes("refused") || low.includes("unreachable") || low.includes("getaddrinfo")) {
    return "No se ha podido contactar con el servidor de correo (problema de red, no de tu contraseña). Inténtalo de nuevo en unos minutos; si persiste, avisa a Sistemas.";
  }
  return raw;
}

export function ConnectEmailDialog({ open, onClose, onConnected, currentEmail }) {
  const [form, setForm] = useState({ ...GMAIL_PRESET, email: currentEmail || "", password: "", display_name: "", signature: "" });
  const [tested, setTested] = useState(false);
  const [guideOpen, setGuideOpen] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => { if (open) { setForm((s) => ({ ...s, email: s.email || currentEmail || "", password: "" })); setTested(false); setError(""); } }, [open, currentEmail]);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setProvider = (p) => { setForm((s) => ({ ...s, ...(PRESETS[p] || { provider: "custom" }) })); setTested(false); setError(""); setGuideOpen(true); };
  const payload = () => ({ ...form, password: form.password.replace(/\s+/g, ""), email: form.email.trim() });
  const guide = PROVIDER_GUIDE[form.provider] || PROVIDER_GUIDE.custom;

  const testMut = useMutation({
    mutationFn: () => api.post("/asistente-trip/email-account/test", payload()),
    onSuccess: () => { setTested(true); setError(""); toast.success("Conexión correcta ✓ Ya puedes pulsar «Conectar»"); },
    onError: (e) => { setTested(false); setError(friendlyConnectError(e, form.provider)); },
  });
  const connectMut = useMutation({
    mutationFn: () => api.post("/asistente-trip/email-account/connect", payload()),
    onSuccess: () => { toast.success("Cuenta de correo conectada. No te la volveremos a pedir."); onConnected?.(); onClose(); },
    onError: (e) => setError(friendlyConnectError(e, form.provider)),
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto" data-testid="connect-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5" /> Conectar mi correo</DialogTitle>
          <DialogDescription>Para que las reservas, re-confirmaciones y solicitudes de factura salgan desde <strong>tu</strong> dirección, SOFI necesita conectarse a tu buzón una sola vez.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>¿Dónde tienes el correo?</Label>
              <Select value={form.provider} onValueChange={setProvider}>
                <SelectTrigger data-testid="connect-provider"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gmail">Gmail / Google Workspace</SelectItem>
                  <SelectItem value="outlook">Outlook / Microsoft 365</SelectItem>
                  <SelectItem value="ionos">IONOS</SelectItem>
                  <SelectItem value="zoho">Zoho</SelectItem>
                  <SelectItem value="custom">Otro (manual)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Nombre visible</Label><Input value={form.display_name} onChange={(e) => set("display_name", e.target.value)} placeholder="Marina · Viaja de Verdad" data-testid="connect-display-name" /></div>
          </div>

          <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-950" data-testid="connect-guide">
            <button type="button" onClick={() => setGuideOpen((o) => !o)} className="flex w-full items-center justify-between text-left font-medium" data-testid="connect-guide-toggle">
              <span className="flex items-center gap-2"><AlertCircle className="h-4 w-4" /> ¿Qué es la «contraseña de aplicación» y cómo la consigo?</span>
              {guideOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            {guideOpen && (
              <div className="mt-2 space-y-2">
                <p className="leading-relaxed">{guide.what}</p>
                {guide.link && (
                  <a href={guide.link} target="_blank" rel="noopener noreferrer" data-testid="connect-guide-link"
                    className="inline-flex items-center gap-1.5 rounded-md bg-sky-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-800">
                    <Plug className="h-3.5 w-3.5" /> {guide.linkLabel}
                  </a>
                )}
                {guide.steps.length > 0 && (
                  <ol className="list-decimal space-y-1 pl-5 text-[13px] leading-relaxed" data-testid="connect-guide-steps">
                    {guide.steps.map((st, i) => <li key={i}>{st}</li>)}
                  </ol>
                )}
                {guide.note && <p className="text-xs text-sky-800/80">{guide.note}</p>}
              </div>
            )}
          </div>

          <div className="space-y-1.5"><Label>Tu correo</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="tu@viajadverdad.com" data-testid="connect-email" /></div>
          <div className="space-y-1.5">
            <Label>{form.provider === "ionos" ? "Contraseña del buzón" : "Contraseña de aplicación"}</Label>
            <Input type="password" value={form.password} onChange={(e) => { set("password", e.target.value); setTested(false); setError(""); }}
              placeholder={form.provider === "gmail" ? "abcd efgh ijkl mnop (16 letras, con o sin espacios)" : "Pega aquí la contraseña generada"} data-testid="connect-password" />
            <p className="text-xs text-muted-foreground">Se guarda cifrada en SOFI. Puedes revocarla en cualquier momento desde tu cuenta de correo.</p>
          </div>
          {form.provider === "custom" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>SMTP host</Label><Input value={form.smtp_host} onChange={(e) => set("smtp_host", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>SMTP puerto</Label><Input type="number" value={form.smtp_port} onChange={(e) => set("smtp_port", parseInt(e.target.value) || 465)} /></div>
              <div className="space-y-1.5"><Label>IMAP host</Label><Input value={form.imap_host} onChange={(e) => set("imap_host", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>IMAP puerto</Label><Input type="number" value={form.imap_port} onChange={(e) => set("imap_port", parseInt(e.target.value) || 993)} /></div>
            </div>
          )}
          {error && <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" data-testid="connect-error">{error}</div>}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => testMut.mutate()} disabled={testMut.isPending || !form.email || !form.password} data-testid="connect-test-button">
            <Plug className="mr-1.5 h-4 w-4" />{testMut.isPending ? "Probando…" : "1 · Probar conexión"}
          </Button>
          <Button onClick={() => connectMut.mutate()} disabled={connectMut.isPending || !tested} data-testid="connect-save-button">
            {connectMut.isPending ? "Conectando…" : "2 · Conectar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AsistenteTab({ tripId }) {
  const qc = useQueryClient();
  const [showConnect, setShowConnect] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [collapsedDays, setCollapsedDays] = useState(new Set());
  const [expandedThread, setExpandedThread] = useState(null);
  const [composer, setComposer] = useState(null); // { reservaIds, providerId, toEmail, providerName, multi }

  const reservasQ = useQuery({ queryKey: ["asistente-reservas", tripId], queryFn: async () => (await api.get(`/asistente-trip/${tripId}/reservas`)).data });
  const emailsQ = useQuery({ queryKey: ["asistente-emails", tripId], queryFn: async () => (await api.get(`/asistente-trip/${tripId}/emails`)).data });

  const data = reservasQ.data;
  const reservas = useMemo(() => (data?.reservas || []).filter((r) => r.type !== "free_day"), [data]);
  const emailConnected = !!data?.email_connected;

  // Map reserva_id -> correos enviados
  const sentByReserva = useMemo(() => {
    const m = {};
    (emailsQ.data?.sent || []).forEach((e) => {
      (e.reserva_ids || []).forEach((rid) => { (m[rid] = m[rid] || []).push(e); });
    });
    return m;
  }, [emailsQ.data]);

  // Agrupar por día (date_entry)
  const days = useMemo(() => {
    const groups = {};
    reservas.forEach((r) => { const k = r.date_entry || ""; (groups[k] = groups[k] || []).push(r); });
    return Object.entries(groups).sort(([a], [b]) => (a || "9999").localeCompare(b || "9999"));
  }, [reservas]);

  const isMailReady = (r) => r.provider_sistema === "MAIL" && !!r.provider_email && !r.booked_by_kimkim;

  const toggleSelect = (r) => {
    setSelected((prev) => { const next = new Set(prev); next.has(r.id) ? next.delete(r.id) : next.add(r.id); return next; });
  };
  const toggleDay = (k) => setCollapsedDays((prev) => { const next = new Set(prev); next.has(k) ? next.delete(k) : next.add(k); return next; });

  const selectedReservas = reservas.filter((r) => selected.has(r.id));
  const selectedProviderIds = new Set(selectedReservas.map((r) => r.provider_id).filter(Boolean));

  const onSendMulti = () => {
    if (selectedProviderIds.size !== 1) { toast.error("Las reservas seleccionadas deben ser del mismo proveedor"); return; }
    const first = selectedReservas[0];
    setComposer({ multi: true, reservaIds: selectedReservas.map((r) => r.id), providerId: first.provider_id, toEmail: first.provider_email, providerName: first.provider_name });
  };

  const onCloseComposer = (refresh) => {
    setComposer(null);
    if (refresh) {
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["asistente-reservas", tripId] });
      qc.invalidateQueries({ queryKey: ["asistente-emails", tripId] });
      qc.invalidateQueries({ queryKey: ["trip", tripId] });
    }
  };

  if (reservasQ.isLoading) return <div className="space-y-3"><Skeleton className="h-12 w-full" /><Skeleton className="h-72 w-full" /></div>;

  return (
    <div className="space-y-4" data-testid="asistente-tab">
      {/* Cabecera + estado de correo */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Operaciones · Reservas</p>
          <h2 className="font-heading text-xl font-semibold flex items-center gap-2"><Mailbox className="h-5 w-5 text-primary" /> Asistente de Reservas</h2>
          <p className="text-sm text-muted-foreground">Reservas día a día. Marca varias del mismo proveedor para enviarlas en un solo correo.</p>
        </div>
        {emailConnected ? (
          <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-sm" data-testid="email-connected-banner">
            <span className="h-2 w-2 rounded-full bg-emerald-500" /> Enviando como <span className="font-medium">{data.sender_email}</span>
            <Button variant="ghost" size="sm" className="ml-1 h-7" onClick={() => setShowConnect(true)} data-testid="change-email-button">Cambiar</Button>
          </div>
        ) : (
          <Button onClick={() => setShowConnect(true)} data-testid="connect-email-button"><Mail className="mr-1.5 h-4 w-4" />Conectar mi correo</Button>
        )}
      </div>

      {!emailConnected && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="email-warning">
          <AlertCircle className="h-4 w-4" /> Conecta tu correo para poder enviar los emails a proveedores.
        </div>
      )}

      {/* Barra de selección */}
      {selected.size > 0 && (
        <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 rounded-lg border bg-card px-3 py-2 shadow-sm" data-testid="selection-bar">
          <span className="text-sm font-medium">{selected.size} reserva(s) seleccionada(s)</span>
          {selectedProviderIds.size > 1 && <span className="text-xs text-red-600">· proveedores distintos</span>}
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setSelected(new Set())} data-testid="clear-selection-button">Limpiar</Button>
            <Button size="sm" onClick={onSendMulti} disabled={!emailConnected || selectedProviderIds.size !== 1} data-testid="send-multi-button">
              <Mails className="mr-1.5 h-4 w-4" />Enviar correo conjunto ({selected.size})
            </Button>
          </div>
        </div>
      )}

      {reservas.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="asistente-empty">
          Este viaje no tiene reservas todavía. Añádelas en la pestaña "Reservas".
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {days.map(([dayKey, rows]) => {
            const collapsed = collapsedDays.has(dayKey);
            return (
              <Card key={dayKey || "nofecha"} className="overflow-hidden" data-testid={`day-group-${dayKey || "nofecha"}`}>
                <button
                  onClick={() => toggleDay(dayKey)}
                  className="flex w-full items-center gap-2 border-b bg-muted/40 px-4 py-2.5 text-left transition-colors hover:bg-muted/70"
                  data-testid={`day-header-${dayKey || "nofecha"}`}
                >
                  {collapsed ? <ChevronRight className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                  <Calendar className="h-4 w-4 text-primary" />
                  <span className="font-semibold text-sm">{fmtDayHeader(dayKey)}</span>
                  <span className="text-xs text-muted-foreground">· {rows.length} servicio(s)</span>
                </button>
                {!collapsed && (
                  <div className="divide-y">
                    {rows.map((r) => {
                      const Icon = TYPE_ICON[r.type] || MapPin;
                      const dot = statusDot(r.status_reserva);
                      const mailReady = isMailReady(r);
                      const sent = sentByReserva[r.id] || [];
                      const n = sent.length;
                      return (
                        <div key={r.id} data-testid={`reserva-row-${r.id}`}>
                          <div className="flex items-center gap-3 px-4 py-2.5">
                            <Checkbox
                              checked={selected.has(r.id)}
                              disabled={!mailReady}
                              onCheckedChange={() => toggleSelect(r)}
                              data-testid={`select-reserva-${r.id}`}
                            />
                            <span className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ring-2 ${dot.cls}`} title={dot.label} />
                            <span className="w-12 shrink-0 font-mono text-xs text-muted-foreground">{r.hour_entry || "—"}</span>
                            <span className="flex w-24 shrink-0 items-center gap-1 text-xs text-muted-foreground">
                              <Icon className="h-3.5 w-3.5" /> {RESERVA_TYPE_LABELS[r.type] || r.type}
                            </span>
                            <span className="flex-1 truncate text-sm font-medium" title={r.service}>{r.service}</span>
                            <div className="hidden w-44 shrink-0 md:block">
                              <div className="truncate text-sm">{r.provider_name || <span className="text-red-600 text-xs">sin proveedor</span>}</div>
                              {r.provider_sistema && (
                                <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] uppercase tracking-wider ${r.provider_sistema === "MAIL" ? "bg-sky-50 text-sky-700" : "bg-muted text-muted-foreground"}`}>{r.provider_sistema}</span>
                              )}
                            </div>
                            <span className={`hidden shrink-0 rounded px-1.5 py-0.5 text-[11px] lg:inline-block ${RESERVA_STATUS_CLASS[r.status_reserva]}`}>{RESERVA_STATUS_LABELS[r.status_reserva]}</span>
                            <button
                              onClick={() => n > 0 && setExpandedThread(expandedThread === r.id ? null : r.id)}
                              className={`flex w-10 shrink-0 items-center justify-center gap-0.5 rounded-full border px-2 py-0.5 text-[11px] ${n > 0 ? "border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100" : "border-transparent text-muted-foreground/40"}`}
                              data-testid={`thread-toggle-${r.id}`}
                              title={n > 0 ? "Ver correos enviados" : "Sin correos"}
                            >
                              <MessageSquare className="h-3 w-3" />{n || ""}
                            </button>
                            <div className="w-28 shrink-0 text-right">
                              {r.booked_by_kimkim ? (
                                <span className="inline-flex items-center rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold text-orange-700" title="Lo reserva KimKim · no contactar proveedor" data-testid={`booked-kk-${r.id}`}>Booked by KimKim</span>
                              ) : mailReady ? (
                                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!emailConnected}
                                  onClick={() => setComposer({ multi: false, reservaIds: [r.id], providerId: r.provider_id, toEmail: r.provider_email, providerName: r.provider_name })}
                                  data-testid={`send-mail-${r.id}`}>
                                  <Mail className="mr-1 h-3 w-3" />Enviar
                                </Button>
                              ) : r.provider_id ? (
                                <span className="text-[11px] text-muted-foreground" title={r.provider_sistema ? `Sistema: ${r.provider_sistema}` : "Sin email de reservas"}>
                                  {r.provider_sistema && r.provider_sistema !== "MAIL" ? "No es Mail" : "Sin email"}
                                </span>
                              ) : null}
                            </div>
                          </div>
                          {expandedThread === r.id && n > 0 && (
                            <div className="space-y-2 bg-muted/30 px-6 py-3" data-testid={`thread-${r.id}`}>
                              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground"><MessageSquare className="h-3 w-3" /> Correos enviados a este proveedor</div>
                              {sent.slice().sort((a, b) => (a.created_at || "").localeCompare(b.created_at || "")).map((e) => (
                                <div key={e.id} className="rounded-lg border border-sky-200 bg-sky-50/40 px-3 py-2 text-xs">
                                  <div className="flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground">
                                    <span className="font-medium uppercase text-sky-700"><Send className="mr-0.5 inline h-3 w-3" />Enviado</span>
                                    <span>{(e.created_at || "").slice(0, 16).replace("T", " ")}</span>
                                    <span className="truncate">a {e.to_email}</span>
                                  </div>
                                  <div className="mt-0.5 font-medium text-foreground">{e.subject}</div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            );
          })}

          <div className="rounded-lg border border-dashed border-orange-300 bg-orange-50/50 p-3 text-xs text-orange-800">
            <strong>Etapa 2 (próximamente):</strong> la bandeja detectará las respuestas del proveedor y la IA actualizará la reserva (precio, disponibilidad, confirmación).
          </div>
        </div>
      )}

      {composer && (
        <TripEmailComposer
          open={!!composer}
          onClose={onCloseComposer}
          tripId={tripId}
          providerId={composer.providerId}
          reservaIds={composer.reservaIds}
          toEmail={composer.toEmail}
          providerName={composer.providerName}
          multi={composer.multi}
          emailConnected={emailConnected}
          senderEmail={data?.sender_email}
        />
      )}

      <ConnectEmailDialog
        open={showConnect}
        onClose={() => setShowConnect(false)}
        currentEmail={data?.sender_email}
        onConnected={() => qc.invalidateQueries({ queryKey: ["asistente-reservas", tripId] })}
      />
    </div>
  );
}
